import { createAction, props } from "@ngrx/store";
import { TwoFactorAuthMethod } from "@obg/ngx.core";

import { CommunicationChannel, TwoFactorAuthMethodType } from "./two-factor-auth.const";
import {
	RequestOtpPayload,
	TwoFactorAuthDevice,
	TwoFactorAuthEnabledMethods,
	TwoFactorAuthResponse,
	TwoFactorChallengeDetails,
	VerifyPasswordResponse,
	TwoFactorVerifyPayload,
} from "./two-factor-auth.model";

const ACTION_PREFIX = "[Two Factor Auth]";

export const TWO_FACTOR_AUTH_ACTION_TYPE = {
	updateOtpCode: `${ACTION_PREFIX} Update One Time Password`,
	requestOTP: `${ACTION_PREFIX} Request One Time Password`,
	retryRequestOTP: `${ACTION_PREFIX} Retry Request One Time Password`,
	requestOTPSuccess: `${ACTION_PREFIX} Request One Time Password Success`,
	requestOTPFailure: `${ACTION_PREFIX} Request One Time Password Failure`,
	verifyOTP: `${ACTION_PREFIX} Verify One Time Password`,
	verifyOTPSuccess: `${ACTION_PREFIX} Verify One Time Password Success`,
	verifyOTPFailure: `${ACTION_PREFIX} Verify One Time Password Failure`,
	setDefaultVerifyLoginPasswordState: `${ACTION_PREFIX} Set default Verify Login Password State`,
	setSelectedMethod: `${ACTION_PREFIX} Set Selected Method`,
	setEnabledMethods: `${ACTION_PREFIX} Set Enabled Methods`,
	toggleSelectedMethod: `${ACTION_PREFIX} Toggle Selected Method`,
	verifyLoginPassword: `${ACTION_PREFIX} Verify Login Password`,
	verifyLoginPasswordSuccess: `${ACTION_PREFIX} Verify Login Password Success`,
	verifyLoginPasswordFailure: `${ACTION_PREFIX} Verify Login Password Failure`,
	resetOnSuspiciousLogin: `${ACTION_PREFIX} Reset On Suspicious Login`,
	operateAuthMethod: `${ACTION_PREFIX} Operate Auth Method`,
	operateAuthMethodSuccess: `${ACTION_PREFIX} Operate Auth Method Success`,
	operateAuthMethodFailure: `${ACTION_PREFIX} Operate Auth Method Failure`,
	resetOperationType: `${ACTION_PREFIX} Reset Operation Type`,
	resetErrorCode: `${ACTION_PREFIX} Reset Error Code`,
	resetOtpExpiredErrorCode: `${ACTION_PREFIX} Reset OTP Expired Error Code`,
	resetTemporaryErrorCode: `${ACTION_PREFIX} Reset Temporary Error Code`,
	updateChallengeDetails: `${ACTION_PREFIX} Update Challenge Details`,
	reLoginUser: `${ACTION_PREFIX} ReLogin User`,
	checkRememberedDevice: `${ACTION_PREFIX} Check If Device Is Remembered`,
	loadCurrentDevice: `${ACTION_PREFIX} Load Current Device`,
	setCurrentDevice: `${ACTION_PREFIX} Set Current Device`,
	enableCurrentDevice: `${ACTION_PREFIX} Enable Current Device`,
	forgetDevice: `${ACTION_PREFIX} Forget Device`,
	forgetDeviceSuccess: `${ACTION_PREFIX} Forget Device Success`,
	deviceFailure: `${ACTION_PREFIX} Device Failure`,
	resetOnLogout: `${ACTION_PREFIX} Reset On Logout`,
	verifyOTPAccountNotVerified: `${ACTION_PREFIX} Verify OTP Account Not Verified`,
	verifyOTPAccountNotVerifiedFailure: `${ACTION_PREFIX} Verify OTP Account Not Verified Failed`,
	setIsBusy: `${ACTION_PREFIX} Set Is Busy`,
};

export namespace TwoFactorAuthAction {
	export const setSelectedMethod = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.setSelectedMethod,
		props<{ method: TwoFactorAuthMethod | undefined }>()
	);

	export const setEnabledMethods = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.setEnabledMethods,
		props<{ methods: TwoFactorAuthEnabledMethods[] }>()
	);

	export const toggleSelectedMethod = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.toggleSelectedMethod);

	export const requestOTP = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.requestOTP,
		props<{ payload?: RequestOtpPayload }>()
	);

	export const retryRequestOTP = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.retryRequestOTP,
		props<{ payload?: RequestOtpPayload }>()
	);

	export const requestOTPSuccess = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.requestOTPSuccess,
		props<{
			payload: TwoFactorAuthResponse;
			communicationChannel: CommunicationChannel;
		}>()
	);

	export const requestOTPFailure = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.requestOTPFailure,
		props<{ errorCode: string }>()
	);

	export const verifyOTP = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.verifyOTP, props<{ oneTimePassword: string }>());

	export const verifyOTPSuccess = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.verifyOTPSuccess);

	export const verifyOTPFailure = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.verifyOTPFailure,
		props<{ errorCode: string }>()
	);

	export const setDefaultVerifyLoginPasswordState = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.setDefaultVerifyLoginPasswordState
	);

	export const verifyLoginPassword = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.verifyLoginPassword,
		props<{ password: string }>()
	);

	export const verifyLoginPasswordSuccess = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.verifyLoginPasswordSuccess,
		props<{ response: VerifyPasswordResponse }>()
	);

	export const verifyLoginPasswordFailure = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.verifyLoginPasswordFailure,
		props<{ errorCode: string }>()
	);

	export const resetOnSuspiciousLogin = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.resetOnSuspiciousLogin);

	export const resetOnLogout = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.resetOnLogout);

	export const operateAuthMethod = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.operateAuthMethod,
		props<{
			operationType: TwoFactorAuthMethodType;
			token?: string;
		}>()
	);

	export const operateAuthMethodSuccess = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.operateAuthMethodSuccess,
		props<{
			response: TwoFactorAuthEnabledMethods[];
			operationType: TwoFactorAuthMethodType;
			method: TwoFactorAuthMethod;
		}>()
	);

	export const operateAuthMethodFailure = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.operateAuthMethodFailure,
		props<{
			errorCode: string;
			method: TwoFactorAuthMethod;
			remainingAttempts?: number;
			operationType: TwoFactorAuthMethodType;
		}>()
	);

	export const resetOperationType = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.resetOperationType);

	export const resetErrorCode = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.resetErrorCode);

	export const resetOtpExpiredErrorCode = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.resetOtpExpiredErrorCode);

	export const resetTemporaryErrorCode = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.resetTemporaryErrorCode);

	export const updateChallengeDetails = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.updateChallengeDetails,
		props<{ challengeDetails: TwoFactorChallengeDetails; remainingAttempts?: number }>()
	);

	export const reLoginUser = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.reLoginUser);

	export const loadCurrentDevice = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.loadCurrentDevice);

	export const setCurrentDevice = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.setCurrentDevice,
		props<{ currentDevice: TwoFactorAuthDevice }>()
	);

	export const enableCurrentDevice = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.enableCurrentDevice,
		props<{ isEnabled: boolean }>()
	);

	export const checkRememberedDevice = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.checkRememberedDevice);

	export const forgetDevice = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.forgetDevice);

	export const forgetDeviceSuccess = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.forgetDeviceSuccess);

	export const deviceFailure = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.deviceFailure,
		props<{ errorCode: string }>()
	);

	export const verifyOTPAccountNotVerified = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.verifyOTPAccountNotVerified,
		props<{ twoFactorVerifyPayload: TwoFactorVerifyPayload }>()
	);

	export const verifyOTPAccountNotVerifiedFailure = createAction(
		TWO_FACTOR_AUTH_ACTION_TYPE.verifyOTPAccountNotVerifiedFailure,
		props<{ errorCode: string; remainingRequests?: number }>()
	);

	export const setIsBusy = createAction(TWO_FACTOR_AUTH_ACTION_TYPE.setIsBusy, props<{ isBusy: boolean }>());
}

export type requestOTPAction = ReturnType<typeof TwoFactorAuthAction.requestOTP>;
export type requestActionFail = ReturnType<
	typeof TwoFactorAuthAction.verifyLoginPasswordFailure | typeof TwoFactorAuthAction.operateAuthMethodFailure
>;
